// İSG Portal — Kontrol aşaması (DepartmentReview) departman onay API + tipleri.
// **İSG uzmanı tarafı** (web-expert). AI onayını geçen izin (SPEC §7→§8) Kontrol aşamasına düşer;
// firma onay-routing'ine göre gerekli departman(lar) (İSG/Hekim/İK) kendi sorumlu oldukları
// belgeleri inceleyip **onaylar/reddeder**. Tüm gerekli departman onaylayınca izin
// `AwaitingEntryPermit`'e geçer (SPEC §8).
//
// KAYNAK: api-contract.md **§31.4/§31.5** (canlı; backend 095 develop'ta). Uçlar
// `[ModuleAction("permits", "department-review")]` taşır — `approve`'dan AYRI aksiyon (Hekim/İK
// `permits/approve` taşımaz; owner `permits/department-review`'i hekim + hr-expert + İSG rollerine
// verir). **Departman-rol kapısı backend'dedir**: kullanıcı yalnız KENDİ departmanı için karar verir
// (hekim→Hekim; hr-expert→Ik; isg-expert/isg-manager→Isg); aksi → `403 DEPARTMENT_FORBIDDEN`.
//
// **Kapsamlı detay (§31.5):** `GET /permits/{uuid}/department-review?department=…` **kendi kendine
// yeterli** bir görünüm döndürür (izin + sorumlu belgeler + personel + eğitim/araç[yalnız İSG] +
// AI notları + onay durumları). Departman **yalnız sorumlu olduğu** belgeleri görür (Hekim yalnız
// Hekim-belgesi). FE bu ucun döndürdüğünü gösterir — §14 `getPermit` KULLANILMAZ (grant/kapsam ayrı).

import { api } from './client';
import type { DocumentTypeRef } from './documentTypes';
import type { PermitApprovalDepartment } from './permitApprovalRouting';
import type {
  RequiredTrainingDto,
  WorkPermitActivityDto,
  WorkPermitDto,
  WorkPermitPersonnelDto,
  WorkPermitVehicleDto,
} from './permits';

/**
 * Bir (izin, departman) onay durumu — §31.5 (`PermitDepartmentApprovalDto.status`, PascalCase wire).
 * `Pending` (bekliyor) · `Approved` (onayladı) · `Rejected` (reddetti → izin Revision'a).
 */
export type PermitDepartmentApprovalStatus = 'Pending' | 'Approved' | 'Rejected';

/**
 * Bir departmanın bu izindeki onay satırı — §31.5 (`PermitDepartmentApprovalDto`). `department` =
 * onaylayan departman; `status` = güncel durum; `decidedByName` = onaylayan/reddeden kişinin adı
 * (bekliyorsa `null`); `decidedAt` = karar anı (UTC ISO — §4; bekliyorsa `null`); `note` = onay/ret
 * notu (yoksa `null`); `escalatedFrom` = bu satır eskalasyonla mı geldi (kaynak departman; yoksa
 * `null`). Sıralı iç `Id` sızmaz.
 */
export interface PermitDepartmentApprovalDto {
  uuid: string;
  department: PermitApprovalDepartment;
  status: PermitDepartmentApprovalStatus;
  decidedByName?: string | null;
  decidedAt?: string | null;
  note?: string | null;
  escalatedFrom?: PermitApprovalDepartment | null;
}

/**
 * Departman-kapsamlı Kontrol detayı — §31.5 (`DepartmentReviewDetailDto`; **kendi kendine yeterli**).
 * `department` = detayın kapsamlandığı (çağıranın seçtiği/rolünden) departman. `responsibleDocuments`
 * = bu departmanın sorumlu olduğu belge türleri (routing'e göre; Hekim yalnız Hekim-belgeleri).
 * `personnel`/`trainings`/`vehicles`/`activities` = departman kapsamındaki içerik — **eğitimler +
 * araçlar YALNIZ İSG'ye** doludur (diğer departmanlarda `[]`). `activities` = AI notları + sistem-log
 * + yorum (Aksiyonlar). `approvals` = tüm aktif departman onay durumları. İçerik tümü bu tek uçtan
 * gelir (§14 `getPermit` çağrılmaz — departman grant/kapsam ayrı).
 */
export interface DepartmentReviewDetailDto {
  permit: WorkPermitDto;
  department: PermitApprovalDepartment;
  /**
   * Bu departmanın sorumlu olduğu belge türleri (§31.5) — `DocumentTypeRef[]`: personel ve araç
   * kodları karışıktır ve aynı kod iki entity'de olabilir (`Insurance`); sıra §27.0(5) (önce entity).
   */
  responsibleDocuments: DocumentTypeRef[];
  personnel: WorkPermitPersonnelDto[];
  /** Yalnız İSG dolu (diğer departmanlarda `[]`; §31.5). */
  trainings: RequiredTrainingDto[];
  /** Yalnız İSG dolu (diğer departmanlarda `[]`; §31.5). */
  vehicles: WorkPermitVehicleDto[];
  /** AI notları + sistem-log + yorum (Aksiyonlar; §31.3/§31.5). */
  activities: WorkPermitActivityDto[];
  /** Tüm aktif departman onay durumları (§31.5). */
  approvals: PermitDepartmentApprovalDto[];
}

/**
 * Kontrol kuyruğu filtre/sayfa parametreleri — §31.5. Kuyruk departmanı **çağıranın rolünden**
 * çözer (parametre almaz); yalnız `page`/`pageSize` §3 paging.
 */
export interface DepartmentReviewListParams {
  page?: number;
  pageSize?: number;
}

/** Departman onay isteği — §31.4 (`POST .../department-review/approve`). `note` opsiyonel. */
export interface DepartmentReviewApproveRequest {
  department: PermitApprovalDepartment;
  note?: string | null;
}

/**
 * Departman ret isteği — §31.4 (`POST .../department-review/reject`). İzin `Revision`'a düşer.
 * `reason` **zorunlu** (boş → `400 VALIDATION_FAILED`).
 */
export interface DepartmentReviewRejectRequest {
  department: PermitApprovalDepartment;
  reason: string;
}

// ============================================================================
// §31.4/§31.5 — Kontrol aşaması departman onayı (İSG uzmanı; `permits/department-review` grant)
// ============================================================================

/**
 * `GET /permits/department-review` (`department-review`) — çağıranın departman(lar)ı için **aktif
 * bekleyen** onayı olan + `DepartmentReview` statüsündeki izinler (§31.5). Departman üyeliği yoksa
 * boş liste. → `WorkPermitDto[]` (+ total).
 */
export function listDepartmentReview(params: DepartmentReviewListParams = {}) {
  return api.list<WorkPermitDto>('permits/department-review', {
    searchParams: {
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /permits/{uuid}/department-review?department=Isg|Hekim|Ik` (`department-review`) —
 * departman-kapsamlı, kendi kendine yeterli Kontrol detayı (§31.5). Çağıran o departmanın üyesi
 * değil → `403 DEPARTMENT_FORBIDDEN`; geçersiz `department` → `400`; izin yok → `404 PERMIT_NOT_FOUND`.
 * → `DepartmentReviewDetailDto`.
 */
export function getDepartmentReviewDetail(uuid: string, department: PermitApprovalDepartment) {
  return api.get<DepartmentReviewDetailDto>(`permits/${uuid}/department-review`, {
    searchParams: { department },
  });
}

/**
 * `POST /permits/{uuid}/department-review/approve` (`department-review`) — çağıranın departmanı
 * adına izni onaylar. Yetkisiz departman → `403 DEPARTMENT_FORBIDDEN`; Kontrol'de değil / aktif
 * satır yok → `409 PERMIT_INVALID_STATE`. Tüm aktif departman onaylayınca izin `AwaitingEntryPermit`'e
 * geçer (backend). → güncel izin.
 */
export function approveDepartmentReview(uuid: string, body: DepartmentReviewApproveRequest) {
  return api.post<WorkPermitDto>(`permits/${uuid}/department-review/approve`, { json: body });
}

/**
 * `POST /permits/{uuid}/department-review/reject` (`department-review`) — çağıranın departmanı
 * adına izni reddeder → izin `Revision`'a düşer (tedarikçi/açan revize eder + bildirim). `reason`
 * zorunlu (boş → `400`). Yetkisiz departman → `403 DEPARTMENT_FORBIDDEN`. → güncel izin.
 */
export function rejectDepartmentReview(uuid: string, body: DepartmentReviewRejectRequest) {
  return api.post<WorkPermitDto>(`permits/${uuid}/department-review/reject`, { json: body });
}
